import { useState } from 'react';

import { useMe } from '@crehana/auth';
import { useFeatureFlag } from '@crehana/feature-flags/react';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Col, Row } from '@crehana/ui';

import { Layout } from '../../components/CertificationCriteria/Layout';
import useCoursesDependencies from '../../hooks/useCoursesDependencies';
import { useUserTrackCompletionStatus } from '../../hooks/user-track-completion-status';
import type {
  TDetailReward,
  TDetailRewards,
  TDetailTrackBody,
} from '../../types';
import CourseWrapper from '../CardCourses/CourseWrapper';
import { CertificateStatusCard } from '../certificate-status-card';
import ModalListRewardsClaimed from '../Rewards/ModalListRewardsClaimed';
import ModalRewardInProgress from '../Rewards/ModalRewardInProgress';
import RewardCardNew from '../Rewards/RewardCardNew';
import SkillPathCoursesPanelNew from './components/SkillPathCoursesPanelNew';

type LearnTrackCoursesProps = {
  detailTrack: TDetailTrackBody;
  detailReward: TDetailReward;
};

const LearnTrackCourses: React.FC<
  React.PropsWithChildren<LearnTrackCoursesProps>
> = ({ detailTrack, detailReward }) => {
  const { me } = useMe();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenModalListRewards, setIsOpenModalListRewards] =
    useState<boolean>(false);
  const rewardDetail: TDetailRewards = {
    numberCourses: detailReward.numberCourses,
    originalId: detailReward.rewardId,
    status: detailReward.status,
  };

  const sizeHalf =
    detailTrack.listCourses.length === 3
      ? 2
      : Math.floor(detailTrack.listCourses.length / 2);

  const { dependencies, loading: loadingDependencies } = useCoursesDependencies(
    detailTrack.teamId,
  );

  const { flagValue: isTrackCompletionRulesEnabled } = useFeatureFlag(
    'LX_ADMIN_TRACK_COMPLETION_RULES',
  );

  const { requirements } = useUserTrackCompletionStatus({
    organizationTeamId: detailTrack.teamId,
    organizationId: me?.organization?.originalId ?? undefined,
    skip: !isTrackCompletionRulesEnabled,
  });

  // No per-quiz grade exists, so "pending" is the closest the data gets to
  // "failed". listCourses is already ordered by position.
  const pendingQuizCourse = detailTrack.listCourses.find(
    course => course.hasQuiz && !course.hasQuizCompleted,
  );

  const quizCoursesCount = detailTrack.listCourses.filter(
    course => course.hasQuiz,
  ).length;
  const projectCoursesCount = detailTrack.listCourses.filter(
    course => course.hasProject,
  ).length;

  return (
    <div className="tw-w-full">
      <Row>
        <Col xs={12} md={4} lg={4} className="tw-mb-1.2 tw-mx-2 sm:tw-mx-0">
          <Layout
            summaryCourse={{
              progress: detailTrack.inProgressCourses,
              required: detailTrack.mandatoryCoursesCount,
            }}
            summaryProject={{
              progress: detailTrack.inProgressMandatoryProjectsCount,
              required: detailTrack.mandatoryProjectsCount,
            }}
            summaryQuiz={{
              progress: detailTrack.inProgressQuizzesCount,
              required: detailTrack.mandatoryQuizzesCount,
            }}
            teamDisabled={detailTrack.trackDisabled}
            isCompletedTrack={detailTrack.isCompletedTrack}
            trackProjects={detailTrack.trackProjects}
            dataCertificate={detailTrack.dataCertificate}
            hasCertificate={detailTrack.isCertificateGenerated}
            isOpenModality={detailTrack.isOpenModality}
            isTrackCompletionRulesEnabled={isTrackCompletionRulesEnabled}
            completionRequirements={requirements}
            projectCoursesCount={projectCoursesCount}
            quizCoursesCount={quizCoursesCount}
            pendingQuizCourseUrl={
              pendingQuizCourse?.url || pendingQuizCourse?.lastVideoLectureUrl
            }
            isTrackClosed={detailTrack.isRouteClosed}
          />
        </Col>
        <Col xs={12} md={8} lg={8}>
          <SkillPathCoursesPanelNew
            courseCount={detailTrack.listCourses.length}
            className="tw-mb-1.2"
          >
            <ul data-testid="skill-path-courses">
              {detailTrack.listCourses.map((course, index) => {
                return (
                  <CourseWrapper
                    key={Number(course.courseId)}
                    dependencies={dependencies}
                    courseData={course}
                    listCourses={detailTrack.listCourses}
                    loadingDependencies={loadingDependencies}
                    order={index + 1}
                    sizeHalf={sizeHalf}
                    isTrackDisabled={detailTrack.trackDisabled}
                    contentUpdatedAt={detailTrack.contentUpdatedAt}
                    isRouteClosed={detailTrack.isRouteClosed}
                  />
                );
              })}
            </ul>
          </SkillPathCoursesPanelNew>
          {detailTrack.dataCertificate?.attachmentUrl && (
            <CertificateStatusCard
              trackName={detailTrack.nameTrack}
              portraitCertificate={detailTrack.dataCertificate?.image || ''}
              rewardQuantity={detailTrack?.coinPlayroomNumber}
              hasCertificate={detailTrack.isCertificateGenerated}
              attachmentUrl={detailTrack.dataCertificate?.attachmentUrl}
              newCoursesCount={detailTrack.newCoursesCount}
              isCompletedTrack={detailTrack.isCompletedTrack}
            />
          )}
          {detailTrack.shouldRenderRewardCard && (
            <RewardCardNew
              onClickStatusEnable={() => setIsOpenModal(true)}
              onClickStatusTaken={() => setIsOpenModalListRewards(true)}
              detailReward={rewardDetail}
            />
          )}
        </Col>
      </Row>
      <ModalRewardInProgress
        isOpen={isOpenModal}
        onDismiss={() => setIsOpenModal(false)}
      />
      <ModalListRewardsClaimed
        currentTeamId={detailTrack.teamId}
        organizationId={Number(me?.organization?.originalId)}
        isOpen={isOpenModalListRewards}
        onDismiss={() => setIsOpenModalListRewards(false)}
      />
    </div>
  );
};

export default LearnTrackCourses;
